import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { IapStoreSchema } from '../iap-store/iap-store';
import { ProductKeySchema } from '../product-key/product-key';

/** App Store Connect and Play Console both cap an offer code well under this. */
export const OFFER_CODE_MAX_LENGTH = 200;
export const OFFER_CODE_NOTE_MAX_LENGTH = 500;

/**
 * The **only** way a discount a member earned can reach an in-app purchase
 * (`03-payments-iap.md` §Principles).
 *
 * ## Why a mapping table rather than a computed price
 *
 * The direct channel applies a discount natively, because this platform owns the
 * price. IAP prices belong to Apple and Google: the only lever a server has is to
 * hand the client an **offer code** the store already knows about. So a discount
 * rule maps to a code that a staff member created in App Store Connect / Play
 * Console, per product, per store.
 *
 * ## The limitation this table cannot fix, and must therefore state
 *
 * **Amounts are mirrored by hand.** Changing a discount rule from 20% to 30%
 * changes the direct price immediately and changes the IAP price **not at all**
 * — the store's offer code still says 20% until somebody edits it in the console.
 * Nothing here can detect the drift, because the store never tells us what a code
 * is worth. It is a documented store limitation rather than a defect, and
 * {@link OFFER_CODE_MIRRORING_NOTICE} is the sentence the admin surface shows so
 * an operator is told at the moment they can act on it.
 *
 * ## `discountRuleId` and `productKey` carry no foreign keys
 *
 * The rules live in `courses` and may not be read from `billing` (N6); the
 * products are joined on loosely everywhere else for the same reason
 * `user_entitlements.product_key` is. A mapping whose rule or product is gone
 * simply stops matching, which is what a withdrawn plan should do.
 */
export const IapOfferCodeMappingSchema = z
  .object({
    id: UuidSchema,
    discountRuleId: UuidSchema,
    /**
     * The rule's operator name, resolved over the QueryBus at read time. `null`
     * when the rule no longer exists — a staff queue that renders a raw uuid is
     * a defect this repo has already filed twice, and an absent rule is the
     * ordinary case for a withdrawn course.
     */
    discountRuleName: z.string().nullable(),
    productKey: ProductKeySchema,
    /** Likewise resolved, in the caller's locale, and likewise nullable. */
    productName: z.string().nullable(),
    store: IapStoreSchema,
    offerCode: z.string().trim().min(1).max(OFFER_CODE_MAX_LENGTH),
    /** Publication, not deletion: an inactive mapping is never offered. */
    isActive: z.boolean(),
    note: z.string().max(OFFER_CODE_NOTE_MAX_LENGTH).nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .strict();

export type IapOfferCodeMapping = z.infer<typeof IapOfferCodeMappingSchema>;

/**
 * The manual-mirroring warning, in one sentence, kept beside the contract rather
 * than in the admin module so both locales' copy and every future surface quote
 * the same fact. The admin i18n catalogs carry the translated rendering (U8);
 * this is the canonical statement of what is true.
 */
export const OFFER_CODE_MIRRORING_NOTICE =
  'Offer codes are managed by Apple and Google. Changing a discount rule does not change what ' +
  'a code is worth — the amount must be updated by hand in App Store Connect and Play Console.';
